/**
 * JobUploadModal Component
 *
 * Modal for uploading job postings with three input methods:
 * 1. URL (default) - Scrape job posting from URL with preview
 * 2. Paste Text - Paste job description directly
 * 3. Upload File - Upload PDF/DOCX/TXT file
 *
 * All methods validate content before proceeding to ensure it's a job posting.
 */

import React, { useState, useRef } from 'react';
import { Check, Info, Loader2, UploadCloud, X } from 'lucide-react';
import { BaseModal } from './BaseModal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '../../lib/utils';
import { goalsService, ValidateJobContentResponse } from '../../services/goals';
import { toast } from 'sonner';

interface JobUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (file: File) => void;
  onPaste: (data: { title: string; company: string; description: string; source_url?: string }) => void;
  onValidatedContent?: (content: string, preview: ValidateJobContentResponse, source_url?: string) => void;
}

type Tab = 'url' | 'paste' | 'upload';

const TAB_LABELS: Record<Tab, string> = {
  url: 'Paste URL',
  paste: 'Paste Text',
  upload: 'Upload File',
};

export const JobUploadModal: React.FC<JobUploadModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  onPaste,
  onValidatedContent,
}) => {
  // Tab state - URL is default
  const [activeTab, setActiveTab] = useState<Tab>('url');

  // URL tab state
  const [url, setUrl] = useState('');
  const [isScrapingUrl, setIsScrapingUrl] = useState(false);
  const [urlValidationResult, setUrlValidationResult] = useState<ValidateJobContentResponse | null>(null);

  // Paste tab state
  const [title, setTitle] = useState('');
  const [company, setCompany] = useState('');
  const [description, setDescription] = useState('');
  const [isValidatingPaste, setIsValidatingPaste] = useState(false);

  // Upload tab state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isValidatingFile, setIsValidatingFile] = useState(false);
  const [fileValidationResult, setFileValidationResult] = useState<ValidateJobContentResponse | null>(null);

  // Partial extraction data for pre-populating Paste tab
  const [partialExtractionData, setPartialExtractionData] = useState<{
    title?: string;
    company?: string;
    source?: string;
  } | null>(null);

  // Reset state when modal closes
  const handleClose = () => {
    setUrl('');
    setUrlValidationResult(null);
    setTitle('');
    setCompany('');
    setDescription('');
    setSelectedFile(null);
    setFileValidationResult(null);
    setPartialExtractionData(null);
    setActiveTab('url');
    onClose();
  };

  // Switch to Paste tab with optional pre-populated data
  const handleSwitchToPaste = (data?: { title?: string; company?: string; source?: string }) => {
    if (data) {
      setPartialExtractionData(data);
      if (data.title) setTitle(data.title);
      if (data.company) setCompany(data.company);
    }
    setActiveTab('paste');
  };

  // Handle URL scraping
  const handleScrapeUrl = async () => {
    if (!url.trim()) {
      toast.error('Please enter a URL');
      return;
    }

    setIsScrapingUrl(true);
    setUrlValidationResult(null);

    try {
      const result = await goalsService.validateJobContent({
        url: url.trim(),
        source: 'url',
      });

      setUrlValidationResult(result);

      if (!result.is_valid) {
        // Don't show toast - error displayed in preview UI
        // Store partial extraction data if available
        if (result.detected_title || result.detected_company) {
          setPartialExtractionData({
            title: result.detected_title,
            company: result.detected_company,
            source: 'url'
          });
        }
      }
    } catch (error: any) {
      const message = error?.response?.data?.detail || error?.message || 'Failed to scrape URL';
      toast.error(message);
      setUrlValidationResult({
        is_valid: false,
        content_type: 'error',
        message,
        scraped_content: undefined,
      });
    } finally {
      setIsScrapingUrl(false);
    }
  };

  // Handle URL confirm (after successful scrape)
  const handleConfirmUrl = async () => {
    if (urlValidationResult?.scraped_content) {
      if (onValidatedContent) {
        onValidatedContent(urlValidationResult.scraped_content, urlValidationResult, url);
      } else {
        // Fallback: use the paste handler with extracted info
        onPaste({
          title: urlValidationResult.detected_title || 'Job Posting',
          company: urlValidationResult.detected_company || 'Company',
          description: urlValidationResult.scraped_content,
          source_url: url,  // Pass the URL for database storage
        });
      }
      handleClose();
    }
  };

  // Handle paste submit with auto-validation
  const handlePasteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim() || !company.trim() || !description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsValidatingPaste(true);

    try {
      // Validate the pasted content
      const result = await goalsService.validateJobContent({
        content: description,
        source: 'paste',
      });

      if (result.is_valid) {
        onPaste({ title, company, description });
        handleClose();
      } else {
        toast.error(result.message);
      }
    } catch (error: any) {
      // If validation fails, still allow proceeding (fallback behavior)
      console.warn('Validation failed, proceeding anyway:', error);
      onPaste({ title, company, description });
      handleClose();
    } finally {
      setIsValidatingPaste(false);
    }
  };

  // Handle file selection - validate first, show preview
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFile(file);
      setFileValidationResult(null);
      setIsValidatingFile(true);

      try {
        // Validate file content first
        const result = await goalsService.validateJobFile(file);
        setFileValidationResult(result);

        if (!result.is_valid) {
          // Don't show toast - error displayed in preview UI
          // Store partial extraction data if available
          if (result.detected_title || result.detected_company) {
            setPartialExtractionData({
              title: result.detected_title,
              company: result.detected_company,
              source: 'upload'
            });
          }
        }
      } catch (error: any) {
        const message = error?.response?.data?.detail || error?.message || 'Failed to process file';
        toast.error(message);
        setFileValidationResult({
          is_valid: false,
          content_type: 'error',
          message,
          scraped_content: undefined,
        });
      } finally {
        setIsValidatingFile(false);
      }
    }
  };

  // Handle file confirm (after successful validation)
  const handleConfirmFile = async () => {
    if (fileValidationResult?.scraped_content) {
      // Use the same flow as URL/paste - go through onPaste with the extracted content
      onPaste({
        title: fileValidationResult.detected_title || 'Job Posting',
        company: fileValidationResult.detected_company || 'Company',
        description: fileValidationResult.scraped_content,
      });
      handleClose();
    }
  };

  // Render validation result preview
  const renderValidationResult = () => {
    if (!urlValidationResult) return null;

    if (urlValidationResult.is_valid) {
      return (
        <div className="mt-4 rounded-xl border border-success/25 bg-success/10 p-4">
          <div className="mb-3 flex items-center gap-2">
            <Check className="size-5 text-success" />
            <span className="font-medium text-success">Valid job posting detected</span>
          </div>

          {urlValidationResult.detected_title && (
            <p className="mb-1 font-medium text-foreground">
              {urlValidationResult.detected_title}
              {urlValidationResult.detected_company && (
                <span className="text-muted-foreground"> at {urlValidationResult.detected_company}</span>
              )}
            </p>
          )}

          {urlValidationResult.preview && (
            <p className="mt-2 text-sm text-foreground/70">{urlValidationResult.preview}</p>
          )}

          <div className="mt-4 flex gap-3">
            <Button onClick={handleConfirmUrl} size="lg" className="flex-1">
              Confirm & Analyze
            </Button>
            <Button
              variant="ghost"
              size="lg"
              onClick={() => {
                setUrlValidationResult(null);
                setUrl('');
              }}
            >
              Try Different URL
            </Button>
          </div>
        </div>
      );
    } else {
      return (
        <div className="mt-4 rounded-xl border border-destructive/25 bg-destructive/10 p-4">
          <div className="mb-2 flex items-center gap-2">
            <X className="size-5 text-destructive" />
            <span className="font-medium text-destructive">Could not extract job posting</span>
          </div>

          {/* Primary error message */}
          <p className="mb-2 text-sm text-foreground/70">{urlValidationResult.message}</p>

          {/* Standardized fallback instruction */}
          <p className="mb-3 text-xs text-muted-foreground italic">
            Copy and paste the full job description from the employer's website
          </p>

          {/* Partial extraction notice (if metadata detected) */}
          {(urlValidationResult.detected_title || urlValidationResult.detected_company) && (
            <div className="mb-3 rounded-lg border border-border bg-card/80 p-2 text-xs text-muted-foreground">
              <span className="font-medium">Detected:</span>{' '}
              {urlValidationResult.detected_title && <span>{urlValidationResult.detected_title}</span>}
              {urlValidationResult.detected_title && urlValidationResult.detected_company && <span> at </span>}
              {urlValidationResult.detected_company && <span>{urlValidationResult.detected_company}</span>}
              {' '}(will be pre-filled if you paste manually)
            </div>
          )}

          {/* Single CTA: Paste Instead */}
          <Button
            className="mt-4 w-full"
            onClick={() => handleSwitchToPaste({
              title: urlValidationResult.detected_title,
              company: urlValidationResult.detected_company,
              source: 'url'
            })}
          >
            Paste Job Description Instead
          </Button>
        </div>
      );
    }
  };

  return (
    <BaseModal isOpen={isOpen} onClose={handleClose} maxWidth="lg" title="Add Job Posting">
      <div className="space-y-6" data-onboarding="job-upload-modal">
        {/* Tabs */}
        <div className="flex border-b border-border" role="tablist" aria-label="Job posting input method">
          {(Object.keys(TAB_LABELS) as Tab[]).map((tab) => (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={activeTab === tab}
              onClick={() => setActiveTab(tab)}
              className={cn(
                'px-6 py-3 font-medium transition-colors outline-hidden focus-visible:ring-3 focus-visible:ring-ring/50',
                activeTab === tab
                  ? '-mb-px border-b-2 border-primary text-primary'
                  : 'text-muted-foreground hover:text-foreground/85'
              )}
            >
              {TAB_LABELS[tab]}
            </button>
          ))}
        </div>

        {/* URL Tab */}
        {activeTab === 'url' && (
          <div className="space-y-4">
            <p className="text-foreground/70">
              Paste the URL of the job posting and we'll extract the content for you.
            </p>

            <div className="flex gap-3">
              <Input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com/jobs/software-engineer"
                className="h-9 flex-1"
                disabled={isScrapingUrl}
                onKeyDown={(e) => e.key === 'Enter' && !isScrapingUrl && handleScrapeUrl()}
              />
              <Button
                size="lg"
                onClick={handleScrapeUrl}
                disabled={isScrapingUrl || !url.trim()}
                className="min-w-[100px]"
              >
                {isScrapingUrl ? (
                  <>
                    <Loader2 data-icon="inline-start" className="animate-spin" />
                    Fetching…
                  </>
                ) : (
                  'Fetch'
                )}
              </Button>
            </div>

            {renderValidationResult()}

            {!urlValidationResult && (
              <p className="text-sm text-foreground/55">
                Some job sites may block automated access. If this doesn't work, try the "Paste Text" tab instead.
              </p>
            )}
          </div>
        )}

        {/* Paste Tab */}
        {activeTab === 'paste' && (
          <form onSubmit={handlePasteSubmit} className="space-y-4">
            {/* Show notice if fields were pre-populated */}
            {partialExtractionData && (
              <div className="rounded-xl border border-info/25 bg-info/10 p-3">
                <div className="mb-1 flex items-center gap-2">
                  <Info className="size-4 text-info" />
                  <span className="text-sm font-medium text-info">
                    Pre-filled from {partialExtractionData.source === 'url' ? 'URL' : 'file'}
                  </span>
                </div>
                <p className="text-xs text-foreground/70">
                  We detected some information from your {partialExtractionData.source === 'url' ? 'URL' : 'file'}.
                  Please review and paste the full job description below.
                </p>
              </div>
            )}

            <div>
              <label htmlFor="job-paste-title" className="mb-2 block text-sm font-medium text-foreground/85">
                Job Title *
                {partialExtractionData?.title && (
                  <span className="ml-2 text-xs text-primary">(auto-filled)</span>
                )}
              </label>
              <Input
                id="job-paste-title"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  // Clear pre-population notice if user edits
                  if (partialExtractionData && e.target.value !== partialExtractionData.title) {
                    setPartialExtractionData(null);
                  }
                }}
                placeholder="e.g., Senior Software Engineer"
                className="h-9"
                required
              />
            </div>

            <div>
              <label htmlFor="job-paste-company" className="mb-2 block text-sm font-medium text-foreground/85">
                Company *
                {partialExtractionData?.company && (
                  <span className="ml-2 text-xs text-primary">(auto-filled)</span>
                )}
              </label>
              <Input
                id="job-paste-company"
                value={company}
                onChange={(e) => {
                  setCompany(e.target.value);
                  // Clear pre-population notice if user edits
                  if (partialExtractionData && e.target.value !== partialExtractionData.company) {
                    setPartialExtractionData(null);
                  }
                }}
                placeholder="e.g., Tech Corp"
                className="h-9"
                required
              />
            </div>

            <div>
              <label htmlFor="job-paste-description" className="mb-2 block text-sm font-medium text-foreground/85">
                Job Description *
              </label>
              <Textarea
                id="job-paste-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Paste the full job description here..."
                className="min-h-56 resize-none"
                required
              />
            </div>

            <div className="flex gap-3">
              <Button type="submit" size="lg" className="flex-1" disabled={isValidatingPaste}>
                {isValidatingPaste ? (
                  <>
                    <Loader2 data-icon="inline-start" className="animate-spin" />
                    Validating…
                  </>
                ) : (
                  'Confirm & Analyze'
                )}
              </Button>
              <Button type="button" variant="ghost" size="lg" onClick={handleClose}>
                Cancel
              </Button>
            </div>
          </form>
        )}

        {/* Upload Tab */}
        {activeTab === 'upload' && (
          <div className="space-y-4">
            <p className="text-foreground/70">
              Upload a job posting file (PDF, DOCX, or TXT)
            </p>

            {/* Show upload zone if no validation result yet */}
            {!fileValidationResult && (
              <div
                className={cn(
                  'cursor-pointer rounded-2xl border-2 border-dashed border-border bg-card/60 p-12 text-center transition-all hover:border-primary/40 hover:bg-card',
                  isValidatingFile && 'pointer-events-none opacity-50'
                )}
                onClick={() => !isValidatingFile && fileInputRef.current?.click()}
                data-onboarding="job-upload-zone"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept=".pdf,.docx,.doc,.txt"
                  onChange={handleFileSelect}
                />

                {isValidatingFile ? (
                  <>
                    <Loader2 className="mx-auto mb-4 size-16 animate-spin text-primary" />
                    <p className="mb-2 text-xl font-semibold text-foreground">Validating file…</p>
                    <p className="text-sm text-muted-foreground">Extracting and checking content</p>
                  </>
                ) : (
                  <>
                    <UploadCloud className="mx-auto mb-4 size-16 text-primary" strokeWidth={1.5} />
                    <p className="mb-2 text-xl font-semibold text-foreground">Drop file here or click to browse</p>
                    <p className="text-sm text-muted-foreground">Supports PDF, DOCX, DOC, TXT</p>
                  </>
                )}
              </div>
            )}

            {/* Show validation result (success or error) */}
            {fileValidationResult && fileValidationResult.is_valid && (
              <div className="rounded-xl border border-success/25 bg-success/10 p-4">
                <div className="mb-3 flex items-center gap-2">
                  <Check className="size-5 text-success" />
                  <span className="font-medium text-success">Valid job posting detected</span>
                  {selectedFile && (
                    <span className="ml-2 text-sm text-muted-foreground">({selectedFile.name})</span>
                  )}
                </div>

                {fileValidationResult.detected_title && (
                  <p className="mb-1 font-medium text-foreground">
                    {fileValidationResult.detected_title}
                    {fileValidationResult.detected_company && (
                      <span className="text-muted-foreground"> at {fileValidationResult.detected_company}</span>
                    )}
                  </p>
                )}

                {fileValidationResult.preview && (
                  <p className="mt-2 text-sm text-foreground/70">{fileValidationResult.preview}</p>
                )}

                <div className="mt-4 flex gap-3">
                  <Button onClick={handleConfirmFile} size="lg" className="flex-1">
                    Confirm & Analyze
                  </Button>
                  <Button
                    variant="ghost"
                    size="lg"
                    onClick={() => {
                      setFileValidationResult(null);
                      setSelectedFile(null);
                    }}
                  >
                    Try Different File
                  </Button>
                </div>
              </div>
            )}

            {fileValidationResult && !fileValidationResult.is_valid && (
              <div className="rounded-xl border border-destructive/25 bg-destructive/10 p-4">
                <div className="mb-2 flex items-center gap-2">
                  <X className="size-5 text-destructive" />
                  <span className="font-medium text-destructive">Could not extract job posting</span>
                  {selectedFile && (
                    <span className="ml-2 text-sm text-muted-foreground">({selectedFile.name})</span>
                  )}
                </div>

                {/* Primary error message */}
                <p className="mb-2 text-sm text-foreground/70">{fileValidationResult.message}</p>

                {/* Standardized fallback instruction */}
                <p className="mb-3 text-xs text-muted-foreground italic">
                  Copy and paste the full job description from the employer's website
                </p>

                {/* Partial extraction notice (if metadata detected) */}
                {(fileValidationResult.detected_title || fileValidationResult.detected_company) && (
                  <div className="mb-3 rounded-lg border border-border bg-card/80 p-2 text-xs text-muted-foreground">
                    <span className="font-medium">Detected:</span>{' '}
                    {fileValidationResult.detected_title && <span>{fileValidationResult.detected_title}</span>}
                    {fileValidationResult.detected_title && fileValidationResult.detected_company && <span> at </span>}
                    {fileValidationResult.detected_company && <span>{fileValidationResult.detected_company}</span>}
                    {' '}(will be pre-filled if you paste manually)
                  </div>
                )}

                {/* Single CTA: Paste Instead */}
                <Button
                  className="mt-4 w-full"
                  onClick={() => handleSwitchToPaste({
                    title: fileValidationResult.detected_title,
                    company: fileValidationResult.detected_company,
                    source: 'upload'
                  })}
                >
                  Paste Job Description Instead
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </BaseModal>
  );
};
